import React from 'react';
import ReportCard from '../components/ReportCard';
import StackedShareBar from '../components/StackedShareBar';
import { BUCKET_META, GENDER_EPIDEMIOLOGY, MIN_COHORT, SMALL_SAMPLE_NOTE } from '../copy';
import { fmtCount, fmtPct, share, shareAtLeast, tip } from '../format';

const GenderCard = ({ entry }) => {
  const total = entry.total;
  const tooSmall = total > 0 && total < MIN_COHORT;

  const segments = entry.buckets.map((bucket, index) => ({
    key: bucket.flags,
    length: share(bucket.count, total),
    tip: tip(
      `${entry.gender} · ${BUCKET_META[index].label}`,
      `${fmtPct(share(bucket.count, total))} — ${fmtCount(bucket.count)} of ${fmtCount(total)}`
    ),
  }));

  return (
    <ReportCard fileName={`metabolic-${entry.gender.toLowerCase()}`} className='p-[26px]'>
      <div className='text-ts-xs font-semibold uppercase tracking-[0.12em] text-gray-600'>
        {entry.gender}
      </div>

      {tooSmall ? (
        <p className='mt-ds-4 text-ts-md text-gray-600'>{SMALL_SAMPLE_NOTE}</p>
      ) : (
        <>
          <div className='mt-[14px] font-jetbrains-mono text-[44px] font-semibold leading-none tracking-[-0.02em] tabular-nums text-gray-900'>
            {fmtPct(shareAtLeast(entry.buckets, 2))}
          </div>
          <div className='mt-ds-2 text-[14px] text-gray-700'>have 2+ abnormalities</div>
          <div className='mt-ds-5'>
            <StackedShareBar segments={segments} height={22} />
          </div>
          <div className='mt-ds-3 grid grid-cols-4 gap-ds-2 font-jetbrains-mono text-ts-xs text-gray-600'>
            {entry.buckets.map((bucket, index) => (
              <div
                key={bucket.flags}
                className={index === entry.buckets.length - 1 ? 'font-semibold text-ds-rose-dk' : ''}
              >
                {fmtPct(share(bucket.count, total))}
              </div>
            ))}
          </div>
        </>
      )}
    </ReportCard>
  );
};

/**
 * The gender split, as two matched cards plus the epidemiological reading of the gap. The multiplier in
 * the closing line is computed, so it stays true when the gap narrows.
 */
const MetabolicByGender = ({ data }) => {
  const male = data.by_gender.find((entry) => entry.gender === 'Male');
  const female = data.by_gender.find((entry) => entry.gender === 'Female');
  const maleTop = male ? shareAtLeast(male.buckets, 3) : 0;
  const femaleTop = female ? shareAtLeast(female.buckets, 3) : 0;
  const ratio = femaleTop > 0 ? maleTop / femaleTop : null;

  return (
    <div className='mt-ds-9'>
      <h3 className='m-0 mb-[6px] font-fig-extrabold text-[28px] font-bold leading-[1.18] text-gray-900'>
        {maleTop >= femaleTop
          ? 'Men carry consistently higher risk than women'
          : 'Women carry consistently higher risk than men'}
      </h3>
      <p className='m-0 mb-[22px] text-ts-md text-gray-600'>Abnormality burden by gender</p>

      <div className='grid grid-cols-1 gap-ds-4 md:grid-cols-2'>
        {data.by_gender.map((entry) => (
          <GenderCard key={entry.gender} entry={entry} />
        ))}
      </div>

      <div className='mt-ds-4 rounded-ds-lg border border-gray-300 bg-white px-[26px] py-ds-6'>
        <p className='m-0 max-w-[920px] text-[16px] leading-[1.7] text-gray-800' style={{ textWrap: 'pretty' }}>
          {ratio && ratio >= 1 ? (
            <>
              Men are nearly{' '}
              <strong className='font-semibold text-gray-900'>{`${ratio.toFixed(1)}x more likely`}</strong>{' '}
              than women to have 3+ abnormalities ({fmtPct(maleTop)} vs {fmtPct(femaleTop)}). {GENDER_EPIDEMIOLOGY}
            </>
          ) : (
            <>
              At the 3+ threshold the split is {fmtPct(maleTop)} of men against {fmtPct(femaleTop)} of women.{' '}
              {GENDER_EPIDEMIOLOGY}
            </>
          )}
        </p>
      </div>
    </div>
  );
};

export default MetabolicByGender;
